import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Gift, Users, Percent, Star, Zap } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const ServiceOfferPlan = () => {
  const pricingPlans = [
    {
      id: 'regular',
      icon: Star,
      title: 'Regular Service Charges',
      description: 'Complete car wash including foam body wash, interior vacuum & polish, and tire polish.',
      prices: [
        { type: 'Hatchback', price: '₹350' },
        { type: 'Sedan', price: '₹400' },
        { type: 'SUV / Luxury', price: '₹450' },
      ],
      features: ['Foam body wash', 'Interior vacuum & polish', 'Tire polish'],
    },
    {
      id: 'quick',
      icon: Zap,
      title: 'Quick Exterior Rinse',
      description: 'Only outer body wash with foam. Car key is not required.',
      prices: [
        { type: 'Hatchback', price: '₹250' },
        { type: 'Sedan', price: '₹300' },
        { type: 'SUV / Luxury', price: '₹350' },
      ],
      features: ['Outer body foam wash', 'No key required', 'Quick & efficient'],
    },
    {
      id: 'monthly',
      icon: Gift,
      title: 'Monthly Wash Subscription Plan',
      description: 'Best Value for Regular Users. Pay for 4 Washes & Get 1 Wash FREE!',
      prices: [
        { type: 'Hatchback', price: '₹1,400' },
        { type: 'Sedan', price: '₹1,600' },
        { type: 'SUV / Luxury', price: '₹1,800' },
      ],
      features: ['5 washes for price of 4', 'Valid 30 days', 'Non-transferable'],
      highlight: true,
    },
    {
      id: 'special',
      icon: Percent,
      title: 'Special Offers',
      description: 'Save more with our special discount offers.',
      offers: [
        { name: 'Birthday / Anniversary Wash', discount: 'Flat 10% OFF', note: 'Valid on birthday or ±1 day' },
        { name: 'Twin Cars – Same Location', discount: 'Flat 10% OFF on each car', note: '' },
        { name: 'Pre-Booking Offer', discount: '10% OFF', note: 'Minimum 2 days prior booking' },
        { name: 'First-Time Customer', discount: 'Flat 10% OFF', note: 'On first wash only' },
      ],
    },
    {
      id: 'vip',
      icon: Crown,
      title: 'VIP Membership',
      description: 'Priority. Speed. Privilege. Premium service for premium customers.',
      prices: [
        { type: 'Hatchback', price: '₹400' },
        { type: 'Sedan', price: '₹450' },
        { type: 'SUV / Luxury', price: '₹500' },
      ],
      benefits: [
        'No Booking Required – Just call or WhatsApp',
        '15-Minute Response Guarantee',
        'On-Demand Doorstep Service',
        'Top Priority Service',
        'Zero Waiting Time',
        'Dedicated VIP Service Team',
      ],
      highlight: true,
    },
    {
      id: 'referral',
      icon: Users,
      title: 'Reference & Neighbor\'s Cars Wash Offer',
      description: 'Smart Loyalty Program - Refer & Earn. Good neighbors, Great savings!',
      referralOffers: [
        { refs: '1 car reference', discount: '10% off' },
        { refs: '2 cars references', discount: '15% off' },
        { refs: '10 cars reference', discount: '1 car wash FREE' },
      ],
      neighborOffers: [
        { cars: '2 neighbor cars', discount: '10% OFF each' },
        { cars: '3 neighbor cars', discount: '15% OFF each' },
        { cars: '4 neighbor cars', discount: '20% OFF each' },
      ],
      notes: [
        'Cars must belong to neighbors only',
        'Same place, same road, same cross',
        'Washes done together / back-to-back',
        'Valid on regular wash services',
        'Also applicable for tourist cars at taxi stand',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="service-offer-plan-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            Pricing & Plans
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Service & Offer Plan
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto mb-8">
            Transparent pricing with no hidden charges. Choose the plan that fits your needs.
          </p>
          <div className="bg-[#F7931E]/10 border-l-4 border-[#F7931E] px-6 py-4 rounded-r-lg inline-block">
            <p className="font-heading font-semibold text-[#0B2C5D]">
              Surprise free car wash: Once a month, a randomly selected loyal customer receives a free wash!
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {pricingPlans.map((plan) => (
              <AccordionItem 
                key={plan.id} 
                value={plan.id}
                className={`border rounded-2xl overflow-hidden ${plan.highlight ? 'border-[#F7931E] shadow-lg' : 'border-slate-200'}`}
                data-testid={`pricing-${plan.id}`}
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4 text-left">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${plan.highlight ? 'bg-[#F7931E]/10' : 'bg-[#1E88E5]/10'}`}>
                      <plan.icon className={`h-6 w-6 ${plan.highlight ? 'text-[#F7931E]' : 'text-[#1E88E5]'}`} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#0B2C5D]">
                        {plan.title}
                      </h3>
                      <p className="text-sm text-[#64748B]">{plan.description}</p>
                    </div>
                    {plan.highlight && (
                      <span className="ml-auto bg-[#F7931E] text-white text-xs px-3 py-1 rounded-full font-semibold">
                        Popular
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="pt-4 border-t border-slate-100">
                    {/* Regular pricing table */}
                    {plan.prices && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-[#0B2C5D] mb-4">Pricing</h4>
                        <div className="grid grid-cols-3 gap-4">
                          {plan.prices.map((price, idx) => (
                            <div key={idx} className="bg-[#F8FAFC] rounded-xl p-4 text-center">
                              <p className="text-sm text-[#64748B] mb-1">{price.type}</p>
                              <p className="font-heading text-2xl font-bold text-[#F7931E]">{price.price}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Features list */}
                    {plan.features && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-[#0B2C5D] mb-3">Includes</h4>
                        <ul className="space-y-2">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center text-[#64748B]">
                              <svg className="w-5 h-5 text-[#1E88E5] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* VIP Benefits */}
                    {plan.benefits && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-[#0B2C5D] mb-3">Exclusive VIP Benefits</h4>
                        <ul className="space-y-2">
                          {plan.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center text-[#64748B]">
                              <svg className="w-5 h-5 text-[#F7931E] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-[#64748B] mt-4 italic">
                          * Response time subject to service area & traffic. Maximum 1 on-demand wash per day. VIP benefits are non-transferable.
                        </p>
                      </div>
                    )}

                    {/* Special Offers */}
                    {plan.offers && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        {plan.offers.map((offer, idx) => (
                          <div key={idx} className="bg-[#F8FAFC] rounded-xl p-4">
                            <h5 className="font-semibold text-[#0B2C5D] mb-1">{offer.name}</h5>
                            <p className="text-[#F7931E] font-bold">{offer.discount}</p>
                            {offer.note && <p className="text-xs text-[#64748B] mt-1">{offer.note}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Referral Offers */}
                    {plan.referralOffers && (
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-heading font-semibold text-[#0B2C5D] mb-3">Refer & Earn</h4>
                          <div className="grid sm:grid-cols-3 gap-4">
                            {plan.referralOffers.map((offer, idx) => (
                              <div key={idx} className="bg-[#F8FAFC] rounded-xl p-4 text-center">
                                <p className="text-sm text-[#64748B] mb-1">{offer.refs}</p>
                                <p className="font-heading font-bold text-[#F7931E]">{offer.discount}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-heading font-semibold text-[#0B2C5D] mb-3">Neighbor's Cars Wash Offer</h4>
                          <div className="grid sm:grid-cols-3 gap-4 mb-4">
                            {plan.neighborOffers.map((offer, idx) => (
                              <div key={idx} className="bg-[#1E88E5]/5 rounded-xl p-4 text-center">
                                <p className="text-sm text-[#64748B] mb-1">{offer.cars}</p>
                                <p className="font-heading font-bold text-[#1E88E5]">{offer.discount}</p>
                              </div>
                            ))}
                          </div>
                          <div className="bg-slate-50 rounded-xl p-4">
                            <h5 className="font-semibold text-[#0B2C5D] mb-2">Offer Conditions</h5>
                            <ul className="text-sm text-[#64748B] space-y-1">
                              {plan.notes.map((note, idx) => (
                                <li key={idx}>• {note}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Book Your Service?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Choose your preferred service and book your doorstep car wash today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg flex items-center justify-center"
              data-testid="pricing-book-now"
            >
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20book%20a%20car%20wash%20service."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#0B2C5D] px-8 py-4 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Book via WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceOfferPlan;
